<template>
  <div>
    <LayoutContent>
      <SectionTitle :label="$t('label.orderDetails')" />
      <div v-if="loading" class="text-center" style="margin-top: 20%">
        <v-progress-circular indeterminate color="primary"></v-progress-circular>
      </div>
      <div v-else>
        <v-container class="white rounded-lg">
          <v-row no-gutters>
            <v-col :sm="12" :md="4" :cols="12" class="px-5">
              <h5 class="secondary--text text--lighten-2">{{ $t('label.orderId') }}</h5>
              <p class="black--text text-body-2">{{ detail.order.po_number }}</p>
            </v-col>
            <v-divider :vertical="$vuetify.breakpoint.smAndUp" />
            <v-col :sm="12" :md="4" :cols="12" class="px-5">
              <h5 class="secondary--text text--lighten-2">{{ $t('label.orderTime') }}</h5>
              <p class="black--text text-body-2">{{ detail.order.created_at | longDate }}</p>
            </v-col>
            <v-divider vertical v-if="$vuetify.breakpoint.smAndUp" />
            <v-col :sm="12" :md="4" :cols="12" class="px-5">
              <h5 class="secondary--text text--lighten-2">{{ $t('label.paymentTime') }}</h5>
              <p class="black--text text-body-2">{{ detail.order.issue_date | longDate }}</p>
            </v-col>
          </v-row>
        </v-container>
        <v-row class="mt-5">
          <v-col :cols="12">
            <v-card class="white rounded-lg pa-5" outlined tile>
              <div v-if="detail.order.delivery_date">
                <div class="mb-2">
                  <h5 class="secondary--text text--lighten-2">{{ $t('label.deliveryDateAndTime') }}</h5>
                  <p class="black--text text-body-2">{{ detail.order.delivery_date }}</p>
                </div>
                <v-divider />
              </div>
              <div v-if="detail.delivery_address">
                <div class="my-2">
                  <h5 class="secondary--text text--lighten-2">{{ $t('label.deliveryAddress') }}</h5>
                  <p class="black--text text-body-2">{{ detail.delivery_address }}</p>
                </div>
                <v-divider />
              </div>
              <div class="my-2">
                <div class="d-flex flex-row justify-space-between">
                  <h5 class="secondary--text text--lighten-2">{{ $t('label.paymentInformation') }}</h5>
                  <h5 class="secondary--text text--lighten-2">{{ detail.order.currency }}</h5>
                </div>
                <div class="d-flex flex-row justify-space-between">
                  <p class="black--text text-body-2">{{ $t('label.merchandiseTotal') }}:</p>
                  <p class="black--text text-body-2">{{ formatPrice(detail.order.total_amount) }}</p>
                </div>
                <div class="d-flex flex-row justify-space-between">
                  <p class="black--text text-body-2">{{ $t('label.pointRedemption') }}:</p>
                  <p class="black--text text-body-2">-{{ formatPrice(detail.order.point_redemption_amount) }}</p>
                </div>
                <div class="d-flex flex-row justify-space-between">
                  <p class="black--text text-body-2">{{ $t('label.tax') }}{{ detail.order.tax_charge ? `(${detail.order.tax_percentage})` : '' }}:</p>
                  <p class="black--text text-body-2">{{ detail.order.tax_charge ? formatPrice(detail.order.tax_charge) : '-' }}</p>
                </div>
                <div class="d-flex flex-row justify-space-between">
                  <p class="black--text text-body-2">{{ $t('label.deliveryFee') }}:</p>
                  <p class="black--text text-body-2">{{ Number(shippingFee) ? formatPrice(shippingFee) : shippingFee }}</p>
                </div>
                <div class="d-flex flex-row justify-space-between">
                  <p class="text-body-1 primary--text text--darken-1 font-weight-bold">{{ $t('label.totalPayment') }}:</p>
                  <p class="text-body-1 primary--text text--darken-1 font-weight-bold">{{ detail.order.currency }}</p>
                  <p class="text-body-1 primary--text text--darken-1 font-weight-bold">{{ formatPrice(detail.order.final_amount) }}</p>
                </div>
              </div>
              <v-divider v-if="detail.order.payment_method_name" />
              <div class="my-2" v-if="detail.order.payment_method_name">
                <h5 class="secondary--text text--lighten-2">{{ $t('label.paymentMethod') }}</h5>
                <p class="black--text text-body-2">{{ detail.order.payment_method_name }}</p>
              </div>
            </v-card>
          </v-col>
          <v-col>
            <v-card class="white rounded-lg pa-5" outlined tile>
              <h3 class="text-subtitle-1 secondary--text font-weight-bold">{{ $t('label.checkOut') }} ({{ detail.order.items.length }})</h3>
              <div class="d-flex flex-row my-5" v-for="orderItem in detail.order.items" :key="orderItem.order_id">
                <img :src="orderItem.product_image" class="mr-5 order-image rounded-lg elevation-3" />
                <div>
                  <p class="text-subtitle-2 font-weight-bold grey--text">
                    {{ orderItem.product_subcategory_name }}
                  </p>
                  <p class="text-subtitle-2 secondary--text">
                    {{ orderItem.product_name }}
                  </p>
                  <p class="text-subtitle-2 secondary--text">
                    {{ orderItem.product_volume_variation_name }}
                  </p>
                  <p class="text-subtitle-2 secondary--text">
                    {{ orderItem.size }}
                  </p>
                  <p class="text-body secondary--text font-weight-medium">{{ orderItem.currency }} {{ formatPrice(orderItem.unit_price) }}</p>
                  <p class="text-body secondary--text font-weight-medium">{{ $t('label.qty') }}: {{ orderItem.quantity }}</p>
                </div>
              </div>
            </v-card>
          </v-col>
        </v-row>
      </div>
    </LayoutContent>
    <bottom-bar>
      <v-container class="py-3 d-flex">
        <router-link :to="{ name: $constants.ROUTE_NAME.ORDER_DASHBOARD }" class="mx-auto text-decoration-none">
          <v-btn color="white" class="px-10 d-block rounded-pill text-capitalize primary--text text--darken-1 font-weight-medium">
            {{ $t('label.backToMyOrder') }}
          </v-btn>
        </router-link>
      </v-container>
    </bottom-bar>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import LayoutContent from '@/views/layouts/LayoutContent.vue'
import SectionTitle from '@/components/SectionTitle.vue'
import { uiHelper } from '@/utils'

export default {
  props: { poNumber: { required: true } },
  components: { LayoutContent, SectionTitle },
  name: 'Detail',
  data: () => ({
    loading: false,
    detail: null
  }),
  created() {
    this.fetchDetail()
  },
  computed: {
    voucherOrder() {
      return this.detail.order.items.find((item) => item.product_category_id == 7)
    },
    shippingFee() {
      if (this.voucherOrder) {
        return '-'
      }
      return this.detail.order.shipping_charge + this.detail.order.min_charge || this.$t('label.free')
    }
  },
  methods: {
    ...mapActions('order', ['getOrderDetail']),
    fetchDetail() {
      this.loading = true
      this.getOrderDetail(this.poNumber)
        .then((res) => {
          this.detail = res
        })
        .finally(() => (this.loading = false))
    },
    formatPrice(val) {
      return uiHelper.priceFormatter(val)
    }
  }
}
</script>

<style lang="scss" scoped>
.order-image {
  width: 120px;
  height: 120px;
  object-fit: cover;
}
.total-bg {
  background-color: var(--v-secondary-lighten5) !important;
  border-color: var(--v-background-base) !important;
}

.voucher-image {
  width: 200px;
  height: 200px;
  padding: 20px 30px;
  object-fit: contain;
}

.unavailable-item {
  filter: opacity(0.6);
}
</style>
